import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { dcApi } from '../services/dc.api'

export interface DistributionCenter {
  id: number
  code: string
  name: string
  city: string | null
  state: string | null
  is_active: boolean
}

interface DCContextType {
  distributionCenters: DistributionCenter[]
  selectedDC: DistributionCenter | null
  setSelectedDC: (dc: DistributionCenter) => void
  isLoading: boolean
}

const DCContext = createContext<DCContextType | undefined>(undefined)

export function DCProvider({ children }: { children: ReactNode }) {
  const [distributionCenters, setDistributionCenters] = useState<DistributionCenter[]>([])
  const [selectedDC, setSelectedDC] = useState<DistributionCenter | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadDistributionCenters()
  }, [])

  const loadDistributionCenters = async () => {
    try {
      setIsLoading(true)
      const dcs = await dcApi.getDistributionCenters()
      setDistributionCenters(dcs)

      // Load saved DC from localStorage or use first one
      const savedDCId = localStorage.getItem('selectedDCId')
      if (savedDCId) {
        const savedDC = dcs.find((dc: DistributionCenter) => dc.id === parseInt(savedDCId))
        if (savedDC) {
          setSelectedDC(savedDC)
        } else if (dcs.length > 0) {
          setSelectedDC(dcs[0])
        }
      } else if (dcs.length > 0) {
        setSelectedDC(dcs[0])
      }
    } catch (error) {
      console.error('Failed to load distribution centers:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSetSelectedDC = (dc: DistributionCenter) => {
    setSelectedDC(dc)
    localStorage.setItem('selectedDCId', dc.id.toString())
  }

  return (
    <DCContext.Provider
      value={{
        distributionCenters,
        selectedDC,
        setSelectedDC: handleSetSelectedDC,
        isLoading
      }}
    >
      {children}
    </DCContext.Provider>
  )
}

export function useDC() {
  const context = useContext(DCContext)
  if (context === undefined) {
    throw new Error('useDC must be used within a DCProvider')
  }
  return context
}
